/**
 * Project Specification Service
 * Handles all database operations for project specifications
 */

import { supabase } from '@/lib/supabase';
import type { ProjectSpecification, ProjectSpecificationFormData, ProjectSpecificationSubmitPayload,  } from '@/types/project-specification.types';

/**
 * Submit a complete project specification with floor measurements
 */
export async function submitProjectSpecification(
  payload: ProjectSpecificationSubmitPayload
): Promise<{ data: ProjectSpecification | null; error: Error | null }> {
  try {
    console.log('🔧 Service: Starting project specification submission');
    console.log('📋 Payload:', payload);

    // Insert project specification
    console.log('💾 Inserting project specification...');
    const { data: specData, error: specError } = await supabase
      .from('project_specifications')
      .insert({
        ...payload.specification,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (specError) {
      console.error('❌ Database error inserting specification:', specError);
      return { data: null, error: new Error(specError.message) };
    }

    console.log('✅ Project specification inserted successfully:', specData.id);

    // Insert floor measurements with the project_specification_id
    if (payload.floor_measurements.length > 0) {
      console.log(`📏 Inserting ${payload.floor_measurements.length} floor measurements...`);
      const measurementsToInsert = payload.floor_measurements.map((m) => ({
        ...m,
        project_specification_id: specData.id,
      }));

      const { error: measurementsError } = await supabase
        .from('floor_measurements')
        .insert(measurementsToInsert);

      if (measurementsError) {
        console.error('❌ Error inserting floor measurements:', measurementsError);
        // Rollback: delete the specification if measurements fail
        console.log('🔄 Rolling back specification insertion...');
        await supabase.from('project_specifications').delete().eq('id', specData.id);
        return { data: null, error: new Error(measurementsError.message) };
      }

      console.log('✅ Floor measurements inserted successfully');
    }

    // Send email notifications after successful submission
    console.log('📧 Attempting to send email notification...');
    try {
      const { data: { url } } = await supabase.functions.getUrl('send-project-spec-email');
      
      console.log('📬 Email function URL:', url);
      const emailResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          specification: payload.specification,
          floor_measurements: payload.floor_measurements,
        }),
      });

      if (!emailResponse.ok) {
        console.warn('⚠️ Email notification failed, but submission was successful');
        console.warn('📧 Email response status:', emailResponse.status);
        const errorText = await emailResponse.text();
        console.warn('📧 Email error details:', errorText);
      } else {
        console.log('✅ Email notification sent successfully');
      }
    } catch (emailError) {
      // Log email error but don't fail the submission
      console.warn('⚠️ Email notification error (submission still successful):', emailError);
    }

    console.log('🎉 Complete submission successful');
    return { data: specData, error: null };
  } catch (error) {
    console.error('❌ Unexpected service error:', error);
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to submit project specification'),
    };
  }
}

/**
 * Get all project specifications (admin/authenticated users)
 */
export async function getAllProjectSpecifications(): Promise<{
  data: ProjectSpecificationFormData[] | null;
  error: Error | null;
}> {
  try {
    const { data: specs, error: specsError } = await supabase
      .from('project_specifications')
      .select('*')
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (specsError) {
      return { data: null, error: new Error(specsError.message) };
    }

    // Fetch floor measurements for each specification
    const specsWithMeasurements = await Promise.all(
      specs.map(async (spec) => {
        const { data: measurements, error: measurementsError } = await supabase
          .from('floor_measurements')
          .select('*')
          .eq('project_specification_id', spec.id)
          .order('from_floor');

        return {
          ...spec,
          floor_measurements: measurementsError ? [] : measurements,
        };
      })
    );

    return { data: specsWithMeasurements, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to fetch project specifications'),
    };
  }
}

/**
 * Get a single project specification by ID
 */
export async function getProjectSpecificationById(
  id: string
): Promise<{ data: ProjectSpecificationFormData | null; error: Error | null }> {
  try {
    const { data: spec, error: specError } = await supabase
      .from('project_specifications')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .single();

    if (specError) {
      return { data: null, error: new Error(specError.message) };
    }

    // Fetch floor measurements
    const { data: measurements, error: measurementsError } = await supabase
      .from('floor_measurements')
      .select('*')
      .eq('project_specification_id', id)
      .order('from_floor');

    if (measurementsError) {
      return { data: null, error: new Error(measurementsError.message) };
    }

    return {
      data: {
        ...spec,
        floor_measurements: measurements,
      },
      error: null,
    };
  } catch (error) {
    return {
      data: null,
      error:
        error instanceof Error ? error : new Error('Failed to fetch project specification'),
    };
  }
}

/**
 * Update project specification status
 */
export async function updateProjectSpecificationStatus(
  id: string,
  status: string,
  reviewedBy?: string
): Promise<{ data: ProjectSpecification | null; error: Error | null }> {
  try {
    const updateData: any = { status };
    
    if (status === 'reviewed' || status === 'approved') {
      updateData.reviewed_at = new Date().toISOString();
      if (reviewedBy) {
        updateData.reviewed_by = reviewedBy;
      }
    }

    const { data, error } = await supabase
      .from('project_specifications')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { data: null, error: new Error(error.message) };
    }

    return { data, error: null };
  } catch (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to update status'),
    };
  }
}

/**
 * Delete project specification (soft delete)
 */
export async function deleteProjectSpecification(
  id: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    const { error } = await supabase
      .from('project_specifications')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) {
      return { success: false, error: new Error(error.message) };
    }

    return { success: true, error: null };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Failed to delete project specification'),
    };
  }
}